import React, { useEffect, useState } from 'react'
import { Text, View, StyleSheet, TouchableOpacity } from 'react-native'
import { Ionicons, FontAwesome5 } from '@expo/vector-icons'
import moment from 'moment'
import { fontPalette } from '../../../../static/styles/global'

interface ClockCardProps {
  navigation: any
  t: (key: string, options?: any) => string
  shiftid: number | null
  timein: string | null
  isClockInShow: boolean
  isClockInFlag: boolean
  clockstore: any
  setOnBreak: (onBreak: boolean) => void
  setClockIn: (clockIn: boolean) => void
  setClockOut: (clockOut: boolean) => void
}

const ClockInActionsCard: React.FC<ClockCardProps> = ({
  navigation,
  t,
  shiftid,
  timein,
  isClockInShow,
  isClockInFlag,
  clockstore,
  setOnBreak,
  setClockIn,
  setClockOut
}) => {
  const [currentTime, setCurrentTime] = useState(moment().format('h:mm A'))

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment().format('h:mm A'))
    }, 60000)
    return () => clearInterval(timer)
  }, [])

  const statusNum = clockstore?.clockStatusData?.status?.clockinStatusNum
  const latestBreak = clockstore?.clockStatusData?.status?.userLatestBreak
  const allowDeptTransfer = clockstore?.clockStatusData?.status?.allowDeptTransfer
  const userClockInTime = clockstore?.clockStatusData?.status?.userClockInTime
  const userClockOutTime = clockstore?.clockStatusData?.status?.userClockOutTime
  const latestBreakStartTime = clockstore?.clockStatusData?.status?.latestBreakStartTime
  const latestMealStartTime = clockstore?.clockStatusData?.status?.latestMealStartTime

  // Status message and color
  let statusText = t('Time')
  let statusColor = '#64748B' // slate-500
  let isClockedIn = false

  if (isClockInShow && statusNum === 1 && !!shiftid) {
    statusText = t('Ready to Clock In')
    statusColor = '#E2B93B'
  } else if (statusNum === 2) {
    isClockedIn = true
    if (latestBreak === 'BREAK_START') {
      statusText = t('On Break')
      statusColor = '#EF4444' // red-500
    } else if (latestBreak === 'MEAL_START') {
      statusText = t('On Meal Break')
      statusColor = '#EF4444'
    } else {
      statusText = t('Currently Clocked In')
      statusColor = '#10B981' // emerald-500
    }
  } else if (statusNum === 3) {
    statusText = t('Clocked Out')
    statusColor = '#64748B'
  }

  // Renders the right side buttons depending on current clock status
  const renderButtons = () => {
    if (!isClockInFlag) {
      return (
        <View style={styles.noClockInContainer}>
          <Text style={styles.noClockInText}>{t('No Clock In available')}</Text>
        </View>
      )
    }

    // Ready to clock in
    if (isClockInShow && statusNum === 1 && !!shiftid) {
      return (
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => navigation.navigate('ClockIn', { setClockIn, shiftid })}
        >
          <Ionicons name="timer-outline" size={20} color="white" style={styles.btnIcon} />
          <Text style={styles.primaryButtonText}>{t('clockin')}</Text>
        </TouchableOpacity>
      )
    }

    // Clocked In - regular
    if (statusNum === 2 && latestBreak !== 'MEAL_START' && latestBreak !== 'BREAK_START' && !!shiftid) {
      return (
        <View style={styles.btnStack}>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() =>
              navigation.navigate('Takeabreak', {
                handlebreak: setOnBreak,
                shiftid,
                breakType: 'BREAK_START'
              })
            }
          >
            <FontAwesome5 name="coffee" size={16} color="#0081D0" style={styles.btnIcon} />
            <Text style={styles.secondaryButtonText}>{t('Take a Break')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() =>
              navigation.navigate('Takeabreak', {
                handlebreak: setOnBreak,
                shiftid,
                breakType: 'MEAL_START'
              })
            }
          >
            <Ionicons name="restaurant-outline" size={16} color="#0081D0" style={styles.btnIcon} />
            <Text style={styles.secondaryButtonText}>{t('Meal Break')}</Text>
          </TouchableOpacity>

          {allowDeptTransfer && (
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={() => navigation.navigate('Transfer', { shiftid })}
            >
              <Ionicons name="swap-horizontal" size={16} color="#0081D0" style={styles.btnIcon} />
              <Text style={styles.secondaryButtonText}>{t('Transfer')}</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={styles.primaryButton}
            onPress={() =>
              navigation.navigate('ClockOut', {
                setClockIn,
                setClockOut,
                shiftid
              })
            }
          >
            <Ionicons name="time-outline" size={16} color="white" style={styles.btnIcon} />
            <Text style={styles.primaryButtonText}>{t('Clock Out')}</Text>
          </TouchableOpacity>
        </View>
      )
    }

    // On regular break
    if (statusNum === 2 && latestBreak === 'BREAK_START' && !!shiftid) {
      return (
        <View style={styles.btnStack}>
          <Text style={styles.breakText}>
            {t('breakEndsAt', {
              time: moment(latestBreakStartTime).add(10, 'minutes').format('LT')
            })}
          </Text>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => {
              setOnBreak(false)
              navigation.navigate('Endbreak', {
                breaktype: 'BREAK_END',
                shiftid
              })
            }}
          >
            <Ionicons name="play-outline" size={16} color="#0081D0" style={styles.btnIcon} />
            <Text style={styles.secondaryButtonText}>{t('End Break')}</Text>
          </TouchableOpacity>
        </View>
      )
    }

    // On meal break
    if (statusNum === 2 && latestBreak === 'MEAL_START' && !!shiftid) {
      return (
        <View style={styles.btnStack}>
          <Text style={styles.breakText}>
            {t('mealBreakEndsAt', {
              time: moment(latestMealStartTime).add(30, 'minutes').format('LT')
            })}
          </Text>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => {
              setOnBreak(false)
              navigation.navigate('Endbreak', {
                breaktype: 'MEAL_END',
                shiftid
              })
            }}
          >
            <Ionicons name="play-outline" size={16} color="#0081D0" style={styles.btnIcon} />
            <Text style={styles.secondaryButtonText}>{t('End Meal Break')}</Text>
          </TouchableOpacity>
        </View>
      )
    }

    // Clocked out at time
    if (statusNum === 3) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.infoText}>
            {t('You clocked out at')}{' '}
            {moment(userClockOutTime).format('LT')}
          </Text>
        </View>
      )
    }

    return null
  }

  return (
    <View style={styles.card}>
      {/* Left Column: Clock and Status */}
      <View style={styles.leftCol}>
        <View style={styles.statusRow}>
          <View style={[styles.statusIndicator, { backgroundColor: statusColor }]} />
          <Text style={[styles.statusText, { color: statusColor }]}>{statusText}</Text>
        </View>
        <Text style={styles.clockText}>{currentTime}</Text>
        <View style={styles.divider} />
        {statusNum === 2 && userClockInTime && (
          <Text style={styles.clockedInAtText}>
            {t('You clocked in at')}: {moment(userClockInTime).format('LT')}
          </Text>
        )}
      </View>

      {/* Right Column: Actions */}
      <View style={styles.rightCol}>
        {renderButtons()}
      </View>
    </View>
  )
}

export default ClockInActionsCard

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
    marginHorizontal: 16,
    marginVertical: 8
  },
  leftCol: {
    flex: 1.1,
    justifyContent: 'center',
    paddingRight: 8
  },
  rightCol: {
    flex: 0.9,
    justifyContent: 'center',
    alignItems: 'stretch'
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6
  },
  statusText: {
    fontSize: 13,
    fontFamily: fontPalette.primarySemiBold
  },
  clockText: {
    fontSize: 34,
    fontFamily: fontPalette.primaryBold,
    color: '#1E293B',
    letterSpacing: -0.5
  },
  divider: {
    height: 1,
    backgroundColor: '#E2E8F0',
    marginVertical: 8,
    width: '80%'
  },
  clockedInAtText: {
    fontSize: 12,
    color: '#64748B',
    fontFamily: fontPalette.primarySemiBold
  },
  btnStack: {
    gap: 8
  },
  primaryButton: {
    backgroundColor: '#0081D0',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: fontPalette.primarySemiBold,
    marginLeft: 6
  },
  secondaryButton: {
    backgroundColor: '#F0F9FF',
    borderWidth: 1,
    borderColor: '#E0F2FE',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  secondaryButtonText: {
    color: '#0081D0',
    fontSize: 13,
    fontFamily: fontPalette.primarySemiBold,
    marginLeft: 6
  },
  btnIcon: {
    marginRight: 2
  },
  noClockInContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8
  },
  noClockInText: {
    fontSize: 14,
    color: '#64748B',
    fontFamily: fontPalette.primarySemiBold,
    textAlign: 'center'
  },
  breakText: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 4,
    fontFamily: fontPalette.primaryRegular,
  },
  infoText: {
    fontSize: 13,
    color: '#64748B',
    textAlign: 'center',
    fontFamily: fontPalette.primaryRegular,
  },
  centerContainer: {
    justifyContent: 'center',
    alignItems: 'center'
  }
})
